import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Sparkles, Store, ImageOff } from 'lucide-react';
import type { Boutique } from '../../hooks/useBoutiques';
import { getFeaturedHeroImage, handleCarouselKeyDown, nextCarouselIndex } from '../../lib/featuredHero';
import { cn } from '../../lib/utils';

const AUTO_ADVANCE_MS = 6000;

function FallbackImg({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className: string;
}) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={cn(className, 'flex items-center justify-center bg-gray-200')}>
        <ImageOff className="h-8 w-8 text-gray-400" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

interface FeaturedStoreHeroCarouselProps {
  boutiques: Boutique[];
}

export function FeaturedStoreHeroCarousel({ boutiques }: FeaturedStoreHeroCarouselProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const carouselRef = useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<number | null>(null);

  const total = boutiques.length;
  const activeBoutique = boutiques[activeIndex];
  const canRotate = total > 1 && !prefersReducedMotion;

  const goTo = useCallback((index: number) => {
    if (total === 0) return;
    setActiveIndex(index);
  }, [total]);

  const goNext = useCallback(() => {
    setActiveIndex((current) => nextCarouselIndex(current, total, 1));
  }, [total]);

  const goPrev = useCallback(() => {
    setActiveIndex((current) => nextCarouselIndex(current, total, -1));
  }, [total]);

  useEffect(() => {
    if (activeIndex >= total && total > 0) {
      setActiveIndex(0);
    }
  }, [activeIndex, total]);

  useEffect(() => {
    if (!canRotate || isPaused) {
      if (timerRef.current != null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = window.setInterval(goNext, AUTO_ADVANCE_MS);
    return () => {
      if (timerRef.current != null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [canRotate, isPaused, goNext]);

  if (!activeBoutique) return null;

  const heroImage = getFeaturedHeroImage(activeBoutique);

  const handleNavigate = () =>
    navigate(`/boutique/${activeBoutique.id}`, { state: { from: location.pathname } });

  const onCarouselKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const action = handleCarouselKeyDown(event.key, activeIndex, total);
    if (action === 'prev') {
      event.preventDefault();
      goPrev();
    } else if (action === 'next') {
      event.preventDefault();
      goNext();
    } else if (action === 'first') {
      event.preventDefault();
      goTo(0);
    } else if (action === 'last') {
      event.preventDefault();
      goTo(total - 1);
    } else if (action === 'activate') {
      event.preventDefault();
      handleNavigate();
    }
  };

  const pauseProps = {
    onMouseEnter: () => setIsPaused(true),
    onMouseLeave: () => setIsPaused(false),
    onFocusCapture: () => setIsPaused(true),
    onBlurCapture: (event: React.FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setIsPaused(false);
      }
    },
    onTouchStart: () => setIsPaused(true),
    onTouchEnd: () => setIsPaused(false),
  };

  const controls = total > 1 && (
    <>
      <button
        type="button"
        aria-label="Previous featured store"
        onClick={(e) => {
          e.stopPropagation();
          goPrev();
        }}
        className="absolute left-1 md:left-4 top-1/2 -translate-y-1/2 w-7 h-7 md:w-10 md:h-10 rounded-full bg-black/35 text-white flex items-center justify-center hover:bg-black/50 transition-colors z-10"
      >
        <ChevronLeft className="h-4 w-4 md:h-5 md:w-5" />
      </button>
      <button
        type="button"
        aria-label="Next featured store"
        onClick={(e) => {
          e.stopPropagation();
          goNext();
        }}
        className="absolute right-1 md:right-4 top-1/2 -translate-y-1/2 w-7 h-7 md:w-10 md:h-10 rounded-full bg-black/35 text-white flex items-center justify-center hover:bg-black/50 transition-colors z-10"
      >
        <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
      </button>
      <div
        className="absolute bottom-1.5 md:bottom-4 left-0 right-0 md:left-auto md:right-6 flex justify-center md:justify-end gap-1 md:gap-2 z-10"
        role="tablist"
        aria-label="Featured store slides"
      >
        {boutiques.map((boutique, index) => (
          <button
            key={boutique.id}
            type="button"
            role="tab"
            aria-label={`Go to ${boutique.name}`}
            aria-selected={index === activeIndex}
            onClick={(e) => {
              e.stopPropagation();
              goTo(index);
            }}
            className={cn(
              'rounded-full transition-all',
              index === activeIndex
                ? 'h-1.5 w-4 md:h-2 md:w-6 bg-white'
                : 'h-1.5 w-1.5 md:h-2 md:w-2 bg-white/50 hover:bg-white/70'
            )}
          />
        ))}
      </div>
    </>
  );

  return (
    <section
      ref={carouselRef}
      className="mt-2 md:mt-6 outline-none"
      aria-roledescription="carousel"
      aria-label="Featured stores"
      tabIndex={0}
      onKeyDown={onCarouselKeyDown}
      {...pauseProps}
    >
      <p className="sr-only" aria-live="polite">
        Featured store {activeIndex + 1} of {total}: {activeBoutique.name}
      </p>

      {/* Mobile layout */}
      <div
        className="md:hidden relative h-24 rounded-xl overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300 cursor-pointer"
        onClick={handleNavigate}
        role="group"
        aria-roledescription="slide"
        aria-label={`${activeIndex + 1} of ${total}: ${activeBoutique.name}`}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeBoutique.id}
            initial={prefersReducedMotion ? false : { opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={prefersReducedMotion ? undefined : { opacity: 0, x: -24 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
          >
            <FallbackImg
              src={heroImage}
              alt={`Featured Store - ${activeBoutique.name}`}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
            <div className="absolute inset-0 flex items-center px-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Sparkles className="h-3 w-3 text-amber-300" />
                  <p className="text-white/80 text-[10px] font-medium uppercase tracking-wider">
                    Featured
                  </p>
                </div>
                <h2 className="text-xl font-serif text-white leading-tight truncate">
                  {activeBoutique.name.toUpperCase()}
                </h2>
              </div>
              <span className="bg-white text-heading text-xs font-semibold px-3 py-1.5 rounded-full flex-shrink-0">
                Shop
              </span>
            </div>
          </motion.div>
        </AnimatePresence>
        {controls}
      </div>

      {/* Desktop layout */}
      <div
        className="hidden md:block relative h-80 rounded-2xl overflow-hidden cursor-pointer bg-gradient-to-br from-gray-200 to-gray-300"
        onClick={handleNavigate}
        role="group"
        aria-roledescription="slide"
        aria-label={`${activeIndex + 1} of ${total}: ${activeBoutique.name}`}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeBoutique.id}
            initial={prefersReducedMotion ? false : { opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={prefersReducedMotion ? undefined : { opacity: 0, x: -40 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
          >
            <FallbackImg
              src={heroImage}
              alt={`Featured Store - ${activeBoutique.name}`}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <p className="text-white/80 text-sm font-medium uppercase tracking-wider mb-1 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-300" />
                Featured Store
              </p>
              <h2 className="text-5xl font-serif text-white mb-3">
                {activeBoutique.name.toUpperCase()}
              </h2>
              <button
                type="button"
                className="bg-white text-heading text-sm font-medium px-4 py-2 rounded hover:bg-gray-100 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigate();
                }}
              >
                SHOP {activeBoutique.name.toUpperCase()}
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
        {controls}
      </div>
    </section>
  );
}

export function FeaturedStoreHeroEmptyState() {
  return (
    <section className="mt-2 md:mt-6">
      <div className="rounded-xl md:rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center">
        <Store className="h-8 w-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-body">Featured boutiques will appear here soon.</p>
      </div>
    </section>
  );
}
